import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, CaretDown } from '@phosphor-icons/react';
import { NODES, currentNodeIndex, captionFor, isClosed } from '../lib/stageModel';

// 6-node progress tracker driven by the HubSpot stage. Office Action / Opposition /
// SOU are NOT nodes — they render as a caption under the relevant node.
// Accepts a full `matter`; falls back to a synthesized one for legacy callers.
export default function StatusTimeline({ matter, currentPhase, journeyPhase, onViewReport }) {
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const m = matter || { status_phase: currentPhase, journey_phase: journeyPhase };
  const closed = isClosed(m);
  const currentIndex = currentNodeIndex(m);
  const caption = captionFor(m);
  const greyed = closed;

  const openReport = (e) => {
    e.stopPropagation();
    onViewReport?.(m);
    if (m.search_report_url) window.open(m.search_report_url, '_blank', 'noopener,noreferrer');
  };

  const nodeColor = (i) => {
    if (greyed) return { bg: '#e4e1da', ring: false, text: '#9a9488' };
    if (i < currentIndex) return { bg: '#141b4d', ring: false, text: '#52525B' };
    if (i === currentIndex) return { bg: '#ffd100', ring: true, text: '#09090B' };
    return { bg: '#ffffff', ring: false, text: '#A1A1AA', border: true };
  };

  return (
    <div className="w-full" data-testid="status-timeline">
      {closed && (
        <p className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: '#9a9488' }} data-testid="tracker-closed">Closed</p>
      )}

      {/* Desktop */}
      <div className="hidden md:block">
        <div className="flex items-start">
          {NODES.map((node, i) => {
            const c = nodeColor(i);
            const done = !greyed && i < currentIndex;
            const current = !greyed && i === currentIndex;
            return (
              <div key={node.key} className="relative flex-1 flex flex-col items-center">
                {i < NODES.length - 1 && (
                  <div className="absolute top-4 left-1/2 w-full h-0.5 -translate-y-1/2"
                    style={{ backgroundColor: (!greyed && i < currentIndex) ? '#141b4d' : (greyed ? '#e4e1da' : '#d4cfc5') }} />
                )}
                <div className="relative z-10" data-testid={`timeline-node-${node.key}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${current ? 'ring-4 ring-[#ffd100]/25' : ''}`}
                    style={{ backgroundColor: c.bg, border: c.border ? '2px solid #d4cfc5' : 'none' }}>
                    {done && <Check weight="bold" className="w-4 h-4 text-white" />}
                    {current && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#141b4d' }} />}
                  </div>
                </div>
                <span className="mt-3 px-1 text-sm font-semibold text-center leading-tight" style={{ color: c.text }}>
                  {node.label}
                </span>
                {caption && caption.nodeIndex === i && (
                  caption.link ? (
                    <button onClick={openReport}
                      className="mt-1 px-1 text-sm text-center leading-tight font-semibold underline hover:no-underline cursor-pointer"
                      style={{ color: '#1d57a5' }}
                      data-testid="timeline-caption-link">
                      {caption.text}
                    </button>
                  ) : (
                    <span className="mt-1 px-1 text-sm text-center leading-tight" style={{ color: greyed ? '#9a9488' : '#1d57a5' }}
                      data-testid="timeline-caption">
                      {caption.text}
                    </span>
                  )
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile — compact progress + expandable node list */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium" style={{ color: '#9a9488' }}>Step {currentIndex + 1} of {NODES.length}</span>
          <span className="text-sm font-semibold" style={{ color: greyed ? '#9a9488' : '#09090B' }}>{NODES[currentIndex]?.label}</span>
        </div>
        <div className="h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: '#e6ddce' }}>
          <div className="h-full rounded-full transition-all duration-500"
            style={{ width: `${(currentIndex / (NODES.length - 1)) * 100}%`, backgroundColor: greyed ? '#9a9488' : '#141b4d' }} />
        </div>
        {caption && (
          caption.link ? (
            <button onClick={openReport}
              className="mt-2 text-sm font-semibold underline hover:no-underline cursor-pointer text-left"
              style={{ color: '#1d57a5' }} data-testid="timeline-caption-link-mobile">{caption.text}</button>
          ) : (
            <p className="mt-2 text-sm" style={{ color: greyed ? '#9a9488' : '#1d57a5' }} data-testid="timeline-caption-mobile">{caption.text}</p>
          )
        )}
        <button onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowMobileDetail(!showMobileDetail); }}
          className="mt-2 inline-flex items-center gap-1 text-sm font-medium" style={{ color: '#1d57a5' }} data-testid="timeline-toggle-stages">
          {showMobileDetail ? 'Hide stages' : 'See all stages'}
          <CaretDown weight="bold" className={`w-3 h-3 transition-transform ${showMobileDetail ? 'rotate-180' : ''}`} />
        </button>
        {showMobileDetail && (
          <div className="flex flex-col mt-3">
            {NODES.map((node, i) => {
              const done = !greyed && i < currentIndex;
              const current = !greyed && i === currentIndex;
              const c = nodeColor(i);
              return (
                <div key={node.key} className="flex items-start">
                  <div className="flex flex-col items-center mr-4">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${current ? 'ring-4 ring-[#ffd100]/25' : ''}`}
                      style={{ backgroundColor: c.bg, border: c.border ? '2px solid #d4cfc5' : 'none' }}>
                      {done && <Check weight="bold" className="w-3 h-3 text-white" />}
                      {current && <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#141b4d' }} />}
                    </div>
                    {i < NODES.length - 1 && <div className="w-0.5 h-6" style={{ backgroundColor: done ? '#141b4d' : '#E4E4E7' }} />}
                  </div>
                  <div className="pb-4">
                    <span className="text-sm font-semibold" style={{ color: c.text }}>{node.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
